import React, { createContext, useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    login,
    getCurrentUser,
    checkSession,
    validateToken,
} from "../services/authService";

// Helper to check if we're running in a browser
const isBrowser = typeof window !== "undefined";

interface User {
    id: string;
    email: string;
    talent_id: string;
    country: string;
    profile: {
        name: string;
    };
}

interface AuthContextType {
    user: User | null;
    loading: boolean;
    error: string | null;
    login: (credentials: { email: string; password: string }) => Promise<void>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const location = useLocation();

    const initializeAuth = async () => {
        try {
            setLoading(true);
            setError(null);

            const token = checkSession();
            if (token) {
                const userData = await getCurrentUser(token);
                setUser(userData);
            }
        } catch (error: any) {
            setError("Authentication initialization failed");
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isBrowser) {
            setLoading(false);
            return;
        }

        // This is the core logic fix.
        // If we are not on the root path, redirect there first.
        if (location.pathname !== "/") {
            const token = new URLSearchParams(location.search).get("token");
            const tokenParam = token ? `?token=${token}` : "";
            navigate(`/${tokenParam}`, { replace: true });
            return; // Exit the effect until the redirect is complete.
        }

        // Once we are on the root path, proceed with authentication.
        const urlParams = new URLSearchParams(location.search);
        const tokenFromUrl = urlParams.get("token");

        if (tokenFromUrl) {
            validateToken(tokenFromUrl)
                .then(() => {
                    initializeAuth();
                })
                .catch((err) => {
                    const message =
                        err.response?.data?.detail || "This token is invalid or has expired.";
                    setError(message);
                    setLoading(false);
                });
        } else {
            initializeAuth();
        }
    }, [location, navigate]);

    const loginUser = async (credentials: {
        email: string;
        password: string;
    }) => {
        try {
            setLoading(true);
            setError(null);

            const { access_token } = await login(credentials);

            const userData = await getCurrentUser(access_token);
            setUser(userData);

            if (isBrowser) {
                navigate("/");
            }
        } catch (error: any) {
            setError(error.response?.data?.detail || "Invalid credentials");
        } finally {
            setLoading(false);
        }
    };

    const logoutUser = () => {
        if (isBrowser) {
            localStorage.removeItem("token");
            localStorage.removeItem("sessionStartTime");
        }
        setUser(null);
        setError(null);
        if (isBrowser) {
            navigate("/login");
        }
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                error,
                login: loginUser,
                logout: logoutUser,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
